import {
  OperatingCalendarSchema,
  type CalendarException,
  type OperatingCalendar,
} from "@opus-dt/shared-types";
import { fromShiftForm, toShiftForm, type ShiftForm } from "./time-helpers";

// Montagem/validação do rascunho do calendário. Fonte única reusada pelo editor
// da linha e pelo seletor do cenário. A validação usa o MESMO schema Zod do backend
// (sobreposição de turnos, datas únicas) — nunca reimplementa a regra.

export interface CalendarDraft {
  shifts: ShiftForm[];
  exceptions: CalendarException[];
}

export function toDraft(
  calendar: Pick<OperatingCalendar, "weeklyShifts" | "exceptions"> | null
): CalendarDraft {
  return {
    shifts: (calendar?.weeklyShifts ?? []).map(toShiftForm),
    exceptions: calendar?.exceptions ?? [],
  };
}

export interface DraftResult {
  calendar: OperatingCalendar | null;
  error: string | null; // primeira mensagem de validação (turno inválido, sobreposição, data dup)
}

// Vazio (sem turnos nem exceções) ⇒ calendar null (= 24/7 / remover). Turno com fim==início ⇒
// erro de linha. Caso contrário, valida o conjunto pelo schema compartilhado.
export function fromDraft(draft: CalendarDraft): DraftResult {
  if (draft.shifts.length === 0 && draft.exceptions.length === 0) {
    return { calendar: null, error: null };
  }
  const built: (ReturnType<typeof fromShiftForm> | null)[] = draft.shifts.map(fromShiftForm);
  if (built.some((s) => s === null)) {
    return { calendar: null, error: "Há turnos com início e fim iguais." };
  }
  const candidate = {
    weeklyShifts: built.filter((s): s is NonNullable<typeof s> => s !== null),
    exceptions: draft.exceptions,
  };
  const parsed = OperatingCalendarSchema.safeParse(candidate);
  if (!parsed.success) {
    return { calendar: null, error: parsed.error.issues[0]?.message ?? "Calendário inválido." };
  }
  return { calendar: parsed.data, error: null };
}
